import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  History as HistoryIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { CREATE_CSV_DOWNLOAD_REQUEST } from '../../../graphql/mutations';
import { GET_CSV_DOWNLOAD_REQUEST, LIST_CSV_DOWNLOAD_REQUESTS } from '../../../graphql/queries';
import { uploadService } from '../../../services/uploadService';

type CsvRequestStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

interface CsvDownloadRequest {
  requestId: string;
  companyId: string;
  requestType: string;
  requestData: string;
  status: CsvRequestStatus;
  resultCsvPath?: string | null;
  fileName?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface CsvExportButtonProps {
  companyId: string;
  /** 出力するCSVの種別（バックエンドの requestType と一致させる） */
  requestType: string;
  /** ボタンに表示する文言（例: サマリCSVダウンロード） */
  buttonLabel: string;
  /** 利用分析画面の検索キーワード（OR条件）。requestData にそのまま渡す */
  searchKeywords: string[];
  /** CSVダウンロード権限がない場合は true */
  disabled?: boolean;
  /** ダウンロード履歴アイコン（履歴ダイアログは全種別を表示するため、ページ内に1つで足りる）を表示するか。デフォルト true */
  showHistory?: boolean;
}

const POLL_INTERVAL_MS = 5000;

const STATUS_LABEL: Record<CsvRequestStatus, string> = {
  PENDING: '待機中',
  PROCESSING: '生成中',
  COMPLETED: '完了',
  FAILED: '失敗',
};

const STATUS_COLOR: Record<CsvRequestStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
};

const REQUEST_TYPE_LABEL: Record<string, string> = {
  Evaluation: '評価明細',
  Summary: 'サマリ',
};

const formatDateTime = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** 署名付きURLからダウンロードを開始する（Content-Disposition でファイル名が付与される） */
const triggerDownload = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * CSVをバックエンドで生成し、ダウンロードするボタン。
 * requestType でサマリ・評価明細などの種別を切り替える。
 * 生成リクエスト → ステータスポーリング → 完了で自動ダウンロード、履歴ダイアログを提供する。
 */
const CsvExportButton: React.FC<CsvExportButtonProps> = ({ companyId, requestType, buttonLabel, searchKeywords, disabled, showHistory = true }) => {
  const theme = useTheme();

  // 生成中リクエストの状態
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [createCsvDownloadRequest] = useMutation(CREATE_CSV_DOWNLOAD_REQUEST);

  // 履歴一覧（パネルを開いている間ポーリング）
  const { data: historyData, loading: historyLoading, refetch: refetchHistory, startPolling: startHistoryPolling, stopPolling: stopHistoryPolling } =
    useQuery(LIST_CSV_DOWNLOAD_REQUESTS, {
      variables: { companyId, limit: 50 },
      skip: !companyId || !historyOpen,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (historyOpen && companyId) {
      startHistoryPolling(POLL_INTERVAL_MS);
      return () => stopHistoryPolling();
    }
  }, [historyOpen, companyId, startHistoryPolling, stopHistoryPolling]);

  // 生成中リクエストのステータスポーリング
  const { data: activeData, startPolling, stopPolling } = useQuery(GET_CSV_DOWNLOAD_REQUEST, {
    variables: { requestId: activeRequestId },
    skip: !activeRequestId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!activeRequestId) return;
    startPolling(POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [activeRequestId, startPolling, stopPolling]);

  const downloadByRequestId = useCallback(async (requestId: string, fallbackName?: string) => {
    setDownloadingId(requestId);
    try {
      const { presignedUrl, fileName } = await uploadService.getCsvUrl(requestId);
      triggerDownload(presignedUrl, fileName || fallbackName);
    } catch (error) {
      console.error('CSVのダウンロードに失敗しました:', error);
      setSnackbar({ message: 'CSVのダウンロードに失敗しました。', severity: 'error' });
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // ポーリング結果に応じて完了/失敗を処理
  useEffect(() => {
    const req: CsvDownloadRequest | undefined = activeData?.getCsvDownloadRequest;
    if (!req || req.requestId !== activeRequestId) return;

    if (req.status === 'COMPLETED') {
      stopPolling();
      setActiveRequestId(null);
      setSnackbar({ message: 'CSVの生成が完了しました。ダウンロードを開始します。', severity: 'success' });
      void downloadByRequestId(req.requestId, req.fileName || undefined);
    } else if (req.status === 'FAILED') {
      stopPolling();
      setActiveRequestId(null);
      setSnackbar({ message: 'CSVの生成に失敗しました。', severity: 'error' });
    }
  }, [activeData, activeRequestId, stopPolling, downloadByRequestId]);

  const isGenerating = isCreating || activeRequestId !== null;

  const handleStart = async () => {
    if (!companyId || isGenerating) return;
    setIsCreating(true);
    try {
      const res = await createCsvDownloadRequest({
        variables: {
          input: {
            companyId,
            requestType,
            // 利用分析画面の検索キーワード（OR条件）をそのまま渡す。空配列なら全件
            requestData: JSON.stringify(searchKeywords),
          },
        },
      });
      const requestId = res.data?.createCsvDownloadRequest?.requestId;
      if (!requestId) {
        throw new Error('リクエストの作成に失敗しました');
      }
      setActiveRequestId(requestId);
      setSnackbar({ message: 'CSVの生成を開始しました。完了までお待ちください。', severity: 'success' });
    } catch (error) {
      console.error('CSV生成リクエストの作成に失敗しました:', error);
      setSnackbar({ message: 'CSV生成リクエストの作成に失敗しました。', severity: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const historyItems: CsvDownloadRequest[] = historyData?.listCsvDownloadRequests?.items ?? [];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
          onClick={handleStart}
          disabled={disabled || isGenerating || !companyId}
          sx={{
            bgcolor: theme.palette.smartSkillTalk?.linkBlue || '#1976d2',
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.smartSkillTalk?.linkBlue || '#1976d2',
              opacity: 0.9,
            },
          }}
        >
          {isGenerating ? '生成中…' : buttonLabel}
        </Button>
        {showHistory && (
          <Tooltip title="ダウンロード履歴">
            <span>
              <IconButton
                onClick={() => setHistoryOpen(true)}
                disabled={disabled || !companyId}
                sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2' }}
              >
                <HistoryIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {showHistory && (
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          CSV ダウンロード履歴
          <Tooltip title="更新">
            <span>
              <IconButton onClick={() => refetchHistory()} disabled={historyLoading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading && historyItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : historyItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              ダウンロード履歴がありません。
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F0F7FF' }}>
                    <TableCell>種別</TableCell>
                    <TableCell>作成日時</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell align="center">ダウンロード</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((item) => (
                    <TableRow key={item.requestId} hover>
                      <TableCell>{REQUEST_TYPE_LABEL[item.requestType] || item.requestType}</TableCell>
                      <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[item.status] || item.status}
                          color={STATUS_COLOR[item.status] || 'default'}
                          variant={item.status === 'COMPLETED' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {item.status === 'COMPLETED' ? (
                          <Tooltip title={item.fileName || 'ダウンロード'}>
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => downloadByRequestId(item.requestId, item.fileName || undefined)}
                                disabled={downloadingId === item.requestId}
                              >
                                {downloadingId === item.requestId ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <FileDownloadIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : item.status === 'FAILED' ? (
                          <Typography variant="caption" color="error">失敗</Typography>
                        ) : (
                          <CircularProgress size={18} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default CsvExportButton;
